import React from 'react';
import type {
  CompassCardComponent,
  CompassCardComponentProps,
} from 'react-scale/corp/views/DataCompassV2/types';
import {
  Badge,
  Callout,
  Code,
  Flex,
  FormField,
  Icon,
  Strong,
  Text,
  TextField,
} from '@scale/scaleui-radix';
import ChatApiEndpointsBuilder from 'react-scale/corp/components/QM/taskTemplatesEditor/ChatApiEndpointsBuilder';
import type { ChatApiEndpointWithId, ProjectChatApiEndpointWithId } from '@scale/shared/types/chat';
import useSweapPassAtKCard from './useSweapPassAtKCard';
import { CompassInstructions } from '@scale/compass-ui/components/CardInstructions';
import { faBookOpen, faCircleExclamation, faInfoCircle } from '@fortawesome/pro-solid-svg-icons';

const REQUIRED_COLUMNS = [
  'image_name',
  'problem_statement',
  'instance_id',
  'base_commit',
  'repo_name',
] as const;

const K_MIN = 1;
const K_MAX = 20;

const SweapPassAtKCardComponent: CompassCardComponent = ({ index }: CompassCardComponentProps) => {
  const { context, setK, setConfigS3Uri, setModelConfig } = useSweapPassAtKCard(index);

  const modelConfigValue = context.modelConfig ? [context.modelConfig] : [];
  const kValue = Number.isFinite(context.k) ? (context.k as number) : 3;

  const validationIssues: string[] = [];
  if (!context.modelConfig) {
    validationIssues.push('Select exactly one model endpoint config.');
  }
  if (!Number.isFinite(kValue) || kValue < K_MIN || kValue > K_MAX) {
    validationIssues.push(`K must be an integer between ${K_MIN} and ${K_MAX}.`);
  }
  if (!context.configS3Uri) {
    validationIssues.push('Provide an SWE agent config S3 URI (e.g. s3://.../tool_use.yaml).');
  } else if (!context.configS3Uri.trim().startsWith('s3://')) {
    validationIssues.push('SWE agent config must be an S3 URI starting with "s3://".');
  }

  return (
    <div className="flex flex-col gap-3 p-2">
      <div className="mb-1">
        <CompassInstructions
          header="How to use the SWEAP Pass@K card"
          storageKey="sweapPassAtKCardInstructionsExpanded"
          icon={faBookOpen}
        >
          <Callout.Text>
            <Text as="p" size="2">
              This card runs SWEAP auto-evaluation and computes Pass@K metrics per SWE-bench
              <Code>instance_id</Code>.
            </Text>

            <Text as="p" size="2" className="mt-2">
              <Strong>1. Provide SWEAP-formatted input data:</Strong> Add an Import CSV card before
              this one and ensure the required columns are present.
            </Text>

            <Text as="p" size="2" className="mt-2">
              <Strong>2. Select a model endpoint:</Strong> Choose exactly one endpoint config (URL +
              params). The run will use this model for solution generation.
            </Text>

            <Text as="p" size="2" className="mt-2">
              <Strong>3. Configure K and SWE agent config:</Strong> Set <Code>K</Code> (number of
              attempts per instance) and provide the SWE agent <Code>s3://</Code> config path
              (commonly <Code>tool_use.yaml</Code>).
            </Text>

            <Text as="p" size="2" className="mt-2">
              The config file defines the agent's prompts/templates (system message, task
              instructions, observation formatting) and available tools/commands (bash, file
              editing, etc.) that the agent can use to solve tasks.
            </Text>

            <Text as="p" size="2" className="mt-2">
              <Strong>Output:</Strong> A table with one row per <Code>instance_id</Code>, columns{' '}
              <Code>run1..runK</Code> (per-attempt pass/fail), <Code>pass_count</Code>, and{' '}
              <Code>pass@k</Code>.
            </Text>
          </Callout.Text>
        </CompassInstructions>
      </div>

      {validationIssues.length > 0 && (
        <Callout.Root color="red" variant="soft" role="alert" size="2" className="items-start">
          <Callout.Icon>
            <Icon icon={faCircleExclamation} label="Error" />
          </Callout.Icon>
          <Callout.Text>
            <Text as="p" size="2">
              <Strong>Please fix the following before running:</Strong>
            </Text>
            <ul className="mt-1 list-disc pl-5">
              {validationIssues.map(issue => (
                <li key={issue}>
                  <Text as="span" size="2">
                    {issue}
                  </Text>
                </li>
              ))}
            </ul>
          </Callout.Text>
        </Callout.Root>
      )}

      <Callout.Root color="neutral" variant="soft" size="2" className="items-start">
        <Callout.Icon>
          <Icon icon={faInfoCircle} label="Info" />
        </Callout.Icon>
        <Callout.Text>
          <Text as="p" size="2">
            <Strong>Input CSV requirements</Strong>
          </Text>
          <Flex wrap="wrap" gap="1" className="mt-2">
            {REQUIRED_COLUMNS.map(col => (
              <Badge key={col} variant="soft" color="blue">
                {col}
              </Badge>
            ))}
          </Flex>
        </Callout.Text>
      </Callout.Root>

      <div className="flex flex-col gap-2">
        <ChatApiEndpointsBuilder
          name="Model Configuration"
          description="Select exactly one model endpoint config (URL + params)."
          existingValue={
            modelConfigValue as Array<ChatApiEndpointWithId | ProjectChatApiEndpointWithId>
          }
          required={true}
          updateProperty={(value: Array<ChatApiEndpointWithId | ProjectChatApiEndpointWithId>) => {
            const first = value?.[0] as ChatApiEndpointWithId | undefined;
            setModelConfig(first);
          }}
          maxNumberOfEndpoints={1}
          showTestButton={false}
        />

        <FormField.Root>
          <FormField.Label>K (Pass@K)</FormField.Label>
          <TextField.Root
            type="number"
            inputMode="numeric"
            min={K_MIN}
            max={K_MAX}
            step={1}
            value={String(kValue)}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
              const raw = e.target.value;
              const next = parseInt(raw, 10);
              if (!Number.isFinite(next)) {
                return;
              }
              setK(Math.max(K_MIN, Math.min(K_MAX, next)));
            }}
          />
          <FormField.Description>
            Number of independent solution attempts per problem. Higher K is slower but more
            informative.
          </FormField.Description>
        </FormField.Root>

        <FormField.Root>
          <FormField.Label>SWE agent config (S3 URI)</FormField.Label>
          <TextField.Root
            value={context.configS3Uri || ''}
            placeholder="s3://scale-dev/model-eval/auto-evaluation/swe-agent/config/tool_use.yaml"
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => setConfigS3Uri(e.target.value)}
          />
          <FormField.Description>
            Path to the SWE agent config YAML in S3 (default <Strong>tool_use.yaml</Strong>). It
            defines the agent’s prompts/templates and available tools/commands.
          </FormField.Description>
        </FormField.Root>
      </div>
    </div>
  );
};

export default SweapPassAtKCardComponent;
